import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Table from '../../components/UI/Table';
import Modal from '../../components/UI/Modal';
import { auditAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { Filter, RefreshCw, Eye } from 'lucide-react';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const FiltersCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}33;
  }
`;

const FiltersActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ActionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: capitalize;
  color: ${props => props.color};
  background-color: ${props => props.bg};
`;

const EntityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  background-color: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.secondary};
  text-transform: capitalize;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
`;

const JsonBlock = styled.pre`
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1rem;
  max-height: 280px;
  overflow: auto;
  font-size: 0.85rem;
  margin: 0;
`;

const ChangesContainer = styled.div`
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1rem;
  max-height: 400px;
  overflow: auto;
`;

const ChangeRow = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const FieldName = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const ValueContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const OldValue = styled.span`
  color: ${props => props.theme.colors.error};
  text-decoration: line-through;
  padding: 0.25rem 0.5rem;
  background: ${props => props.theme.colors.error}15;
  border-radius: ${props => props.theme.borderRadius.sm};
`;

const Arrow = styled.span`
  color: ${props => props.theme.colors.text.secondary};
  font-weight: bold;
`;

const NewValue = styled.span`
  color: ${props => props.theme.colors.success};
  padding: 0.25rem 0.5rem;
  background: ${props => props.theme.colors.success}15;
  border-radius: ${props => props.theme.borderRadius.sm};
`;

const SingleValue = styled.span`
  color: ${props => props.theme.colors.text.primary};
  padding: 0.25rem 0.5rem;
  background: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.sm};
  border: 1px solid ${props => props.theme.colors.border.medium};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.text.secondary};
  font-style: italic;
`;

const MODULE_LABELS = {
  news: 'News',
  newses: 'News',
  news_items: 'News',
  event: 'Events',
  events: 'Events',
  publication: 'Publications',
  publications: 'Publications',
  job: 'Jobs',
  jobs: 'Jobs',
  survey: 'Surveys',
  surveys: 'Surveys',
  member: 'Members',
  members: 'Members',
  membership_application: 'Membership Applications',
  membership_applications: 'Membership Applications',
  membership_submission: 'Membership Applications',
  membership_submissions: 'Membership Applications',
  membership_form: 'Membership Forms',
  membership_forms: 'Membership Forms',
  application: 'Applications',
  applications: 'Applications',
};

const ENTITY_OPTIONS = [
  { value: '', label: 'All Records' },
  { value: 'news', label: 'News' },
  { value: 'event', label: 'Events' },
  { value: 'publication', label: 'Publications' },
  { value: 'job', label: 'Jobs' },
  { value: 'survey', label: 'Surveys' },
  { value: 'member', label: 'Members' },
  { value: 'membership_application', label: 'Membership Applications' },
  { value: 'membership_form', label: 'Membership Forms' },
];

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'restored', label: 'Restored' },
];

// Utility function to format field names
const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  
  // Handle common field name patterns
  const replacements = {
    'id': 'ID',
    'url': 'URL',
    'cv': 'CV',
    'pdf': 'PDF',
    'api': 'API',
    'ip': 'IP',
  };
  
  return fieldName
    .split('_')
    .map(word => {
      const lower = word.toLowerCase();
      return replacements[lower] || word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const resolveModuleKey = (log) => {
  if (!log) return null;

  let metadata = log.metadata;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch (error) {
      metadata = null;
    }
  }

  const metadataModule = metadata?.module || metadata?.feature;
  if (metadataModule) {
    return String(metadataModule).toLowerCase();
  }

  if (log.entity_type && log.entity_type.toLowerCase() !== 'model') {
    return log.entity_type.toLowerCase();
  }

  if (log.table_name) {
    return String(log.table_name).toLowerCase();
  }

  return null;
};

const getModuleLabel = (log) => {
  const key = resolveModuleKey(log);
  if (!key) {
    return 'General';
  }

  if (MODULE_LABELS[key]) {
    return MODULE_LABELS[key];
  }

  return formatFieldName(key);
};

// Utility function to format field values
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return <em style={{ color: '#999' }}>Not set</em>;
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <em style={{ color: '#999' }}>Empty list</em>;
    }
    return value.join(', ');
  }
  
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  
  // Check if it's a date string
  if (typeof value === 'string') {
    const dateMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (dateMatch) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString();
        }
      } catch (e) {
        // Not a valid date, continue
      }
    }
  }
  
  // Truncate very long strings
  const str = String(value);
  if (str.length > 100) {
    return str.substring(0, 100) + '...';
  }
  
  return str;
};

// Get changed fields for updates
const getChangedFields = (oldValues, newValues) => {
  const old = oldValues || {};
  const newV = newValues || {};
  
  const changed = [];
  const allKeys = new Set([...Object.keys(old), ...Object.keys(newV)]);
  
  allKeys.forEach(key => {
    const oldVal = old[key];
    const newVal = newV[key];
    
    // Skip if both values are undefined/null
    if (oldVal === undefined && newVal === undefined) {
      return;
    }
    
    // Skip if values are the same
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
      return;
    }
    
    changed.push({
      field: key,
      oldValue: oldVal,
      newValue: newVal,
    });
  });
  
  return changed;
};

const AuditLogs = () => {
  const { theme } = useTheme();
  const [filters, setFilters] = useState({
    search: '',
    entity_type: '',
    action: '',
    date_from: '',
    date_to: '',
  });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
  });

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page, pagination.perPage]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.page,
        per_page: pagination.perPage,
      };
      const response = await auditAPI.getAll(params);
      const payload = response.data;
      const records = payload?.data || payload || [];
      setLogs(records);
      setPagination(prev => ({
        ...prev,
        total: payload?.total ?? records.length,
        lastPage: payload?.last_page ?? 1,
        page: payload?.current_page ?? prev.page,
      }));
    } catch (error) {
      console.error('Failed to load audit logs', error);
      toast.error('Unable to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      entity_type: '',
      action: '',
      date_from: '',
      date_to: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const handleChangePage = (direction) => {
    setPagination(prev => {
      const nextPage = Math.min(
        Math.max(1, prev.page + direction),
        prev.lastPage || 1
      );
      if (nextPage === prev.page) {
        return prev;
      }
      return { ...prev, page: nextPage };
    });
  };

  const actionColors = useMemo(() => ({
    created: { color: theme.colors.success, bg: theme.colors.success + '20' },
    updated: { color: theme.colors.warning, bg: theme.colors.warning + '20' },
    deleted: { color: theme.colors.error, bg: theme.colors.error + '20' },
    restored: { color: theme.colors.info, bg: theme.colors.info + '20' },
  }), [theme.colors]);

  const columns = [
    {
      header: 'Timestamp',
      accessor: 'created_at',
      render: (value) => value ? format(new Date(value), 'dd MMM yyyy HH:mm') : '—',
    },
    {
      header: 'User',
      accessor: 'actor_name',
      render: (value, row) => (
        <div>
          <div>{value || 'System'}</div>
          <small style={{ color: theme.colors.text.secondary }}>
            {row.actor_role || '—'}
          </small>
        </div>
      ),
    },
    {
      header: 'Module',
      accessor: 'entity_type',
      render: (_value, row) => getModuleLabel(row),
    },
    {
      header: 'Entity',
      accessor: 'entity_type',
      render: (value, row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <EntityBadge theme={theme}>{value || 'unknown'}</EntityBadge>
          <span>{row.entity_name || '—'}</span>
        </div>
      ),
    },
    {
      header: 'Action',
      accessor: 'action',
      render: (value) => {
        const palette = actionColors[value] || {
          color: theme.colors.text.primary,
          bg: theme.colors.background.secondary,
        };
        return (
          <ActionBadge color={palette.color} bg={palette.bg}>
            {value}
          </ActionBadge>
        );
      },
    },
    {
      header: 'Description',
      accessor: 'description',
      render: (value) => value || '—',
    },
    {
      header: 'Details',
      accessor: 'id',
      render: (value, row) => (
        <Button
          size="small"
          variant="ghost"
          leftIcon={<Eye size={14} />}
          onClick={(event) => {
            event.stopPropagation();
            handleViewLog(row);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <Header>
        <div>
          <Title theme={theme}>Audit Logs</Title>
          <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
            Track every change made to critical records across the system.
          </p>
        </div>
        <Button variant="outline" leftIcon={<RefreshCw size={16} />} onClick={fetchLogs} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <FiltersCard>
        <FiltersRow>
          <Input
            label="Search"
            placeholder="Search by user or entity"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <div>
            <label style={{ display: 'block', marginBottom: '0.35rem', color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>
              Entity
            </label>
            <Select
              theme={theme}
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
            >
              {ENTITY_OPTIONS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.35rem', color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>
              Action
            </label>
            <Select
              theme={theme}
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              {ACTION_OPTIONS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <Input
            label="From"
            type="date"
            value={filters.date_from}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
          />
          <Input
            label="To"
            type="date"
            value={filters.date_to}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
          />
        </FiltersRow>
        <FiltersActions>
          <Button variant="ghost" leftIcon={<Filter size={16} />} onClick={handleResetFilters}>
            Clear Filters
          </Button>
        </FiltersActions>
      </FiltersCard>

      <Card>
        <Table
          columns={columns}
          data={logs}
          loading={loading}
          emptyMessage="No audit logs found for the selected filters."
        />
        <Pagination>
          <span style={{ color: theme.colors.text.secondary, fontSize: theme.typography.fontSize.sm }}>
            Page {pagination.page} of {pagination.lastPage}
          </span>
          <Button
            variant="outline"
            size="small"
            onClick={() => handleChangePage(-1)}
            disabled={pagination.page <= 1 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={() => handleChangePage(1)}
            disabled={pagination.page >= (pagination.lastPage || 1) || loading}
          >
            Next
          </Button>
        </Pagination>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Audit Log Details"
        size="large"
      >
        {selectedLog && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <strong>Action:</strong> {selectedLog.action}
            </div>
            <div>
              <strong>Entity:</strong> {selectedLog.entity_type} ({selectedLog.entity_name || '—'})
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <strong>Performed By</strong>
                <div>{selectedLog.actor_name || 'System'}</div>
                <div style={{ color: theme.colors.text.secondary }}>{selectedLog.actor_role || '—'}</div>
              </div>
              <div>
                <strong>When</strong>
                <div>{selectedLog.created_at ? new Date(selectedLog.created_at).toLocaleString() : '—'}</div>
                <div style={{ color: theme.colors.text.secondary }}>
                  {selectedLog.ip_address || 'IP n/a'}
                </div>
              </div>
              <div>
                <strong>Request</strong>
                <div>{selectedLog.request_method || '—'}</div>
                <div style={{ color: theme.colors.text.secondary, wordBreak: 'break-all' }}>
                  {selectedLog.url || '—'}
                </div>
              </div>
            </div>
            <div>
              <strong>Description</strong>
              <p style={{ marginTop: '0.25rem' }}>{selectedLog.description || '—'}</p>
            </div>
            <div>
              <strong style={{ display: 'block', marginBottom: '0.75rem' }}>
                {selectedLog.action === 'updated' ? 'Changes Made' : 
                 selectedLog.action === 'created' ? 'Created Fields' :
                 selectedLog.action === 'deleted' ? 'Deleted Record Fields' :
                 'Record Details'}
              </strong>
              <ChangesContainer theme={theme}>
                {(() => {
                  const oldValues = selectedLog.old_values || {};
                  const newValues = selectedLog.new_values || {};
                  
                  if (selectedLog.action === 'updated') {
                    // Show only changed fields for updates
                    const changedFields = getChangedFields(oldValues, newValues);
                    
                    if (changedFields.length === 0) {
                      return (
                        <EmptyState theme={theme}>
                          No changes detected (values may be identical)
                        </EmptyState>
                      );
                    }
                    
                    return changedFields.map((change, idx) => {
                      const hasOld = change.oldValue !== undefined && change.oldValue !== null;
                      const hasNew = change.newValue !== undefined && change.newValue !== null;
                      
                      return (
                        <ChangeRow key={idx} theme={theme}>
                          <FieldName theme={theme}>
                            {formatFieldName(change.field)}
                          </FieldName>
                          <ValueContainer theme={theme}>
                            {hasOld && (
                              <>
                                <OldValue theme={theme}>
                                  {formatValue(change.oldValue)}
                                </OldValue>
                                {hasNew && <Arrow theme={theme}>→</Arrow>}
                              </>
                            )}
                            {hasNew && (
                              <NewValue theme={theme}>
                                {formatValue(change.newValue)}
                              </NewValue>
                            )}
                            {!hasOld && !hasNew && (
                              <em style={{ color: theme.colors.text.secondary }}>No value</em>
                            )}
                          </ValueContainer>
                        </ChangeRow>
                      );
                    });
                  } else if (selectedLog.action === 'created') {
                    // Show all new values for created records
                    const fields = Object.keys(newValues);
                    
                    if (fields.length === 0) {
                      return (
                        <EmptyState theme={theme}>
                          No field data available
                        </EmptyState>
                      );
                    }
                    
                    return fields.map((field, idx) => (
                      <ChangeRow key={idx} theme={theme}>
                        <FieldName theme={theme}>
                          {formatFieldName(field)}
                        </FieldName>
                        <ValueContainer theme={theme}>
                          <SingleValue theme={theme}>
                            {formatValue(newValues[field])}
                          </SingleValue>
                        </ValueContainer>
                      </ChangeRow>
                    ));
                  } else if (selectedLog.action === 'deleted') {
                    // Show old values for deleted records
                    const fields = Object.keys(oldValues);
                    
                    if (fields.length === 0) {
                      return (
                        <EmptyState theme={theme}>
                          No field data available
                        </EmptyState>
                      );
                    }
                    
                    return fields.map((field, idx) => (
                      <ChangeRow key={idx} theme={theme}>
                        <FieldName theme={theme}>
                          {formatFieldName(field)}
                        </FieldName>
                        <ValueContainer theme={theme}>
                          <OldValue theme={theme}>
                            {formatValue(oldValues[field])}
                          </OldValue>
                        </ValueContainer>
                      </ChangeRow>
                    ));
                  } else {
                    // For other actions, show both if available
                    const allFields = new Set([
                      ...Object.keys(oldValues),
                      ...Object.keys(newValues)
                    ]);
                    
                    if (allFields.size === 0) {
                      return (
                        <EmptyState theme={theme}>
                          No field data available
                        </EmptyState>
                      );
                    }
                    
                    return Array.from(allFields).map((field, idx) => (
                      <ChangeRow key={idx} theme={theme}>
                        <FieldName theme={theme}>
                          {formatFieldName(field)}
                        </FieldName>
                        <ValueContainer theme={theme}>
                          {oldValues[field] !== undefined && (
                            <>
                              <OldValue theme={theme}>
                                {formatValue(oldValues[field])}
                              </OldValue>
                              {newValues[field] !== undefined && <Arrow theme={theme}>→</Arrow>}
                            </>
                          )}
                          {newValues[field] !== undefined && (
                            <NewValue theme={theme}>
                              {formatValue(newValues[field])}
                            </NewValue>
                          )}
                        </ValueContainer>
                      </ChangeRow>
                    ));
                  }
                })()}
              </ChangesContainer>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
};

export default AuditLogs;

